import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Loader2, MoreHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/AppLayout.jsx';
import { fetchUserNotifications, markAllUserNotificationsRead, markUserNotificationRead, deleteUserNotification } from '@/api/users';
import { formatRelativeTime, getCategoryMeta, resolveNotificationPath } from '@/utils/notifications';

const SECTION_DEFINITIONS = [
  { key: 'today', label: 'Today' },
  { key: 'recent', label: 'Last 7 days' },
  { key: 'earlier', label: 'Earlier' },
];

const getSectionKey = (createdAt) => {
  if (!createdAt) {
    return 'earlier';
  }

  const target = new Date(createdAt);
  if (Number.isNaN(target.getTime())) {
    return 'earlier';
  }

  const now = new Date();
  const isSameDay = now.getFullYear() === target.getFullYear()
    && now.getMonth() === target.getMonth()
    && now.getDate() === target.getDate();

  if (isSameDay) {
    return 'today';
  }

  const diffInMs = now.getTime() - target.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  if (diffInDays < 7) {
    return 'recent';
  }

  return 'earlier';
};

const groupNotifications = (items) => items.reduce((groups, item) => {
  const key = getSectionKey(item.createdAt);
  if (!groups[key]) {
    groups[key] = [];
  }
  groups[key].push(item);
  return groups;
}, { today: [], recent: [], earlier: [] });

export default function NotificationsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const listRef = useRef(null);
  const [activeItemMenu, setActiveItemMenu] = useState(null);
  const [pendingItemAction, setPendingItemAction] = useState(null);

  const [state, setState] = useState({
    items: [],
    unread: 0,
    total: 0,
    loading: true,
    error: '',
  });

  const loadNotifications = useCallback(async (nextFilter, search) => {
    if (!user?.id) {
      setState({ items: [], unread: 0, total: 0, loading: false, error: '' });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: '' }));

    try {
      const { data } = await fetchUserNotifications(user.id, {
        filter: nextFilter === 'unread' ? 'unread' : 'all',
        search: search || undefined,
        limit: 80,
      });

      const items = Array.isArray(data?.notifications) ? data.notifications : [];
      setState({
        items,
        unread: Number(data?.meta?.unread ?? 0),
        total: Number(data?.meta?.total ?? items.length),
        loading: false,
        error: '',
      });
      setActiveItemMenu(null);
      setPendingItemAction(null);
    } catch (error) {
      console.error('Unable to load notifications', error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: 'Unable to load notifications. Please try again later.',
      }));
      setActiveItemMenu(null);
      setPendingItemAction(null);
    }
  }, [user?.id]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }
    loadNotifications(filter, debouncedSearch);
  }, [user?.id, filter, debouncedSearch, loadNotifications]);

  useEffect(() => {
    setActiveItemMenu(null);
  }, [filter, debouncedSearch, user?.id]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    const handleClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [menuOpen]);

  useEffect(() => {
    if (activeItemMenu === null) {
      return undefined;
    }

    const handleClick = (event) => {
      const host = listRef.current;
      if (!host) {
        setActiveItemMenu(null);
        return;
      }

      if (!host.contains(event.target)) {
        setActiveItemMenu(null);
        return;
      }

      const isTrigger = event.target.closest('[data-notification-menu-trigger]');
      const isPanel = event.target.closest('[data-notification-menu-panel]');

      if (!isTrigger && !isPanel) {
        setActiveItemMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [activeItemMenu]);

  const groupedNotifications = useMemo(() => groupNotifications(state.items), [state.items]);

  const visibleSections = useMemo(
    () => SECTION_DEFINITIONS.filter(({ key }) => groupedNotifications[key]?.length),
    [groupedNotifications],
  );

  const handleNotificationClick = async (notification) => {
    const destination = resolveNotificationPath(notification.linkUrl);

    setActiveItemMenu(null);

    if (user?.id && !notification.isRead) {
      try {
        const { data } = await markUserNotificationRead(user.id, notification.id);
        setState((prev) => ({
          ...prev,
          unread: typeof data?.unread === 'number' ? data.unread : Math.max(0, prev.unread - 1),
          items: prev.items.map((item) => (
            item.id === notification.id ? { ...item, isRead: true } : item
          )),
        }));
      } catch (error) {
        console.error('Unable to mark notification as read', error);
      }
    }

    if (/^https?:\/\//i.test(destination)) {
      window.open(destination, '_blank', 'noopener');
    } else {
      navigate(destination);
    }
  };

  const handleMarkAllRead = async () => {
    if (!user?.id || state.unread === 0) {
      setMenuOpen(false);
      return;
    }
    try {
      await markAllUserNotificationsRead(user.id);
      setState((prev) => ({
        ...prev,
        unread: 0,
        items: prev.items.map((item) => ({ ...item, isRead: true })),
      }));
    } catch (error) {
      console.error('Unable to mark all notifications as read', error);
    } finally {
      setMenuOpen(false);
    }
  };

  const handleItemMenuToggle = (event, notificationId) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveItemMenu((prev) => (prev === notificationId ? null : notificationId));
  };

  const handleItemMarkRead = async (event, notification) => {
    event.preventDefault();
    event.stopPropagation();

    if (!user?.id || notification.isRead || pendingItemAction === notification.id) {
      if (notification.isRead) {
        setActiveItemMenu(null);
      }
      return;
    }

    try {
      setPendingItemAction(notification.id);
      const { data } = await markUserNotificationRead(user.id, notification.id);
      setState((prev) => ({
        ...prev,
        unread: typeof data?.unread === 'number' ? data.unread : Math.max(0, prev.unread - 1),
        items: prev.items.map((item) => (
          item.id === notification.id ? { ...item, isRead: true } : item
        )),
      }));
    } catch (error) {
      console.error('Unable to mark notification as read', error);
    } finally {
      setPendingItemAction(null);
      setActiveItemMenu(null);
    }
  };

  const handleItemDelete = async (event, notification) => {
    event.preventDefault();
    event.stopPropagation();

    if (!user?.id || pendingItemAction === notification.id) {
      return;
    }

    try {
      setPendingItemAction(notification.id);
      const { data } = await deleteUserNotification(user.id, notification.id);
      setState((prev) => {
        const nextItems = prev.items.filter((item) => item.id !== notification.id);
        const inferredUnread = notification.isRead ? prev.unread : Math.max(0, prev.unread - 1);
        const unread = typeof data?.unread === 'number' ? data.unread : inferredUnread;
        const total = Math.max(nextItems.length, Math.max(0, prev.total - 1));

        return {
          ...prev,
          items: nextItems,
          unread,
          total,
        };
      });
    } catch (error) {
      console.error('Unable to delete notification', error);
    } finally {
      setPendingItemAction(null);
      setActiveItemMenu(null);
    }
  };

  const unreadCount = Math.max(0, Number(state.unread) || 0);
  const totalLabel = state.total ? `Showing ${state.items.length} of ${state.total}` : 'No notifications yet.';

  return (
    <AppLayout title="Notifications" user={user} onLogout={onLogout}>
      <section className="notifications-page">
        <div className="notifications-page__header">
          <div>
            <h2>All activity</h2>
            <p>{totalLabel}</p>
          </div>
          <div className="notifications-page__menu" ref={menuRef}>
            <button
              type="button"
              className="notifications-popover__menu-button"
              aria-label="Notification options"
              onClick={() => setMenuOpen((prev) => !prev)}
            >
              <MoreHorizontal size={18} />
            </button>
            {menuOpen && (
              <div className="notifications-popover__menu-panel">
                <button
                  type="button"
                  className="notifications-popover__menu-item"
                  onClick={handleMarkAllRead}
                  disabled={unreadCount === 0}
                >
                  Mark all as read
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="notifications-page__controls">
          <div className="notifications-page__tabs">
            <button
              type="button"
              className={`notifications-page__tab${filter === 'all' ? ' notifications-page__tab--active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              type="button"
              className={`notifications-page__tab${filter === 'unread' ? ' notifications-page__tab--active' : ''}`}
              onClick={() => setFilter('unread')}
            >
              Unread
            </button>
          </div>
          <div className="notifications-page__search">
            <input
              type="search"
              value={searchInput}
              placeholder="Search notifications"
              aria-label="Search notifications"
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </div>
        </div>

        {state.error && <div className="notifications-page__error">{state.error}</div>}

        {state.loading ? (
          <div className="notifications-page__empty">
            <Loader2 size={24} />
            <span>Loading notifications…</span>
          </div>
        ) : state.items.length === 0 ? (
          <div className="notifications-page__empty">
            <p>No notifications to display yet.</p>
          </div>
        ) : (
          <div className="notifications-page__groups" ref={listRef}>
            {visibleSections.map(({ key, label }) => (
              <div className="notifications-page__section" key={key}>
                <h3 className="notifications-page__section-title">{label}</h3>
                <ul className="notifications-popover__list">
                  {groupedNotifications[key].map((item) => {
                    const meta = getCategoryMeta(item.category);
                    const title = item.title?.trim().length ? item.title.trim() : 'Account update';
                    const message = item.message?.trim().length ? item.message.trim() : 'See details in your account.';

                    return (
                      <li key={`${item.id}-${item.linkUrl || ''}`}>
                        <div className={`notifications-popover__row${item.isRead ? '' : ' notifications-popover__row--unread'}`}>
                          <button
                            type="button"
                            className="notifications-popover__item"
                            onClick={() => handleNotificationClick(item)}
                          >
                            <div className="notifications-popover__avatar">{title.charAt(0).toUpperCase()}</div>
                            <div className="notifications-popover__item-body">
                              <div className="notifications-popover__item-head">
                                <p className="notifications-popover__item-title">{title}</p>
                                <span className="notifications-popover__item-time">{formatRelativeTime(item.createdAt)}</span>
                              </div>
                              <p className="notifications-popover__item-message">{message}</p>
                              <div className="notifications-popover__item-meta">
                                <span className={`notifications-popover__badge notifications-popover__badge--${meta.tone}`}>{meta.label}</span>
                                {!item.isRead && <span className="notifications-page__pill">New</span>}
                              </div>
                            </div>
                          </button>
                          <div className={`notifications-popover__item-actions${activeItemMenu === item.id ? ' notifications-popover__item-actions--active' : ''}`}>
                            <button
                              type="button"
                              className="notifications-popover__item-menu"
                              aria-label="Notification options"
                              data-notification-menu-trigger
                              onClick={(event) => handleItemMenuToggle(event, item.id)}
                            >
                              <MoreHorizontal size={16} />
                            </button>
                            {activeItemMenu === item.id && (
                              <div className="notifications-popover__item-menu-panel" data-notification-menu-panel>
                                <button
                                  type="button"
                                  className="notifications-popover__item-menu-item"
                                  onClick={(event) => handleItemMarkRead(event, item)}
                                  disabled={item.isRead || pendingItemAction === item.id}
                                >
                                  Mark as read
                                </button>
                                <button
                                  type="button"
                                  className="notifications-popover__item-menu-item notifications-popover__item-menu-item--danger"
                                  onClick={(event) => handleItemDelete(event, item)}
                                  disabled={pendingItemAction === item.id}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>
    </AppLayout>
  );
}

NotificationsPage.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }).isRequired,
  onLogout: PropTypes.func.isRequired,
};
